import { Descriptions, Popover } from 'antd';
import { format } from 'date-fns';
import { Send } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { type Column, DataTable } from '@/components/common/DataTable';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  type InviteCodeApplication,
  inviteApplicationStore,
} from '@/stores';
import { WaitlistQuestionnaireView } from './WaitlistQuestionnaireView';

interface InviteApplicationTableProps {
  onApprove: (application: InviteCodeApplication) => void;
}

export const InviteApplicationTable = observer(
  ({ onApprove }: InviteApplicationTableProps) => {
    const formatDateTime = (value?: string) =>
      value ? format(new Date(value), 'yyyy-MM-dd HH:mm:ss') : '-';

    const getMailOutboxStatusMeta = (application: InviteCodeApplication) => {
      const outbox = application.latestMailOutbox;
      if (!outbox) {
        if (application.status === 'email_sent') {
          return { label: '发送成功', variant: 'success' as const };
        }
        return { label: '未生成', variant: 'outline' as const };
      }
      switch (outbox.status) {
        case 'pending':
          return { label: '待发送', variant: 'warning' as const };
        case 'sent':
          return { label: '发送成功', variant: 'success' as const };
        case 'failed':
          return { label: '发送失败', variant: 'destructive' as const };
        default:
          if (application.status === 'email_sent') {
            return { label: '发送成功', variant: 'success' as const };
          }
          return { label: '状态未知', variant: 'outline' as const };
      }
    };

    const renderMailOutboxDetail = (application: InviteCodeApplication) => {
      const outbox = application.latestMailOutbox;
      if (!outbox) {
        return (
          <div className="max-w-[360px] text-sm text-muted-foreground">
            当前申请还没有关联的邮件 outbox 记录。
          </div>
        );
      }

      let payloadContent = outbox.payload;
      try {
        payloadContent = JSON.stringify(JSON.parse(outbox.payload), null, 2);
      } catch {
        payloadContent = outbox.payload;
      }

      return (
        <div className="max-w-[420px] space-y-3">
          <Descriptions
            size="small"
            column={1}
            colon={false}
            items={[
              { key: 'status', label: '状态', children: getMailOutboxStatusMeta(application).label },
              { key: 'templateCode', label: '模板', children: outbox.templateCode || '-' },
              { key: 'toEmail', label: '收件人', children: outbox.toEmail || '-' },
              { key: 'retryCount', label: '重试次数', children: outbox.retryCount },
              { key: 'providerMessageId', label: 'Provider Message ID', children: outbox.providerMessageId || '-' },
              { key: 'sentAt', label: '发送时间', children: formatDateTime(outbox.sentAt) },
              { key: 'updatedAt', label: '更新时间', children: formatDateTime(outbox.updatedAt) },
            ]}
          />
          <div className="space-y-1">
            <div className="text-xs font-medium text-muted-foreground">Payload</div>
            <pre className="max-h-48 overflow-auto rounded-md bg-slate-50 p-3 text-xs leading-5 text-slate-700 whitespace-pre-wrap break-all">
              {payloadContent || '-'}
            </pre>
          </div>
          {outbox.lastError ? (
            <div className="space-y-1">
              <div className="text-xs font-medium text-muted-foreground">错误信息</div>
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs leading-5 text-red-700 break-all">
                {outbox.lastError}
              </div>
            </div>
          ) : null}
        </div>
      );
    };

    const columns: Column<InviteCodeApplication>[] = [
      {
        id: 'email',
        header: '邮箱',
        accessor: (row) => <span className="font-medium">{row.email}</span>,
      },
      {
        id: 'userId',
        header: '申请人 User ID',
        accessor: (row) => (
          <span className="font-mono text-sm">{row.userId ?? '-'}</span>
        ),
      },
      {
        id: 'questionnaire',
        header: '问卷摘要',
        accessor: (row) => (
          <div className="min-w-[320px] max-w-[420px] space-y-2">
            {row.questionnaireDisplay ? (
              <>
                <div
                  className="text-sm text-foreground break-words"
                  title={row.questionnaireDisplay.primaryText}
                >
                  {row.questionnaireDisplay.primaryText}
                </div>
                <div
                  className="text-xs text-muted-foreground break-words"
                  title={row.questionnaireDisplay.secondaryText}
                >
                  {row.questionnaireDisplay.secondaryText}
                </div>
              </>
            ) : (
              <div
                className="text-sm text-muted-foreground whitespace-pre-wrap break-all line-clamp-2"
                title={row.reason}
              >
                {row.reason || '-'}
              </div>
            )}
            <Popover
              trigger="click"
              placement="leftTop"
              content={
                <div className="max-w-[420px]">
                  <WaitlistQuestionnaireView
                    questionnaire={row.questionnaire}
                    rawReason={row.reason}
                    compact
                  />
                </div>
              }
            >
              <Button variant="ghost" size="sm" className="px-0">
                查看问卷
              </Button>
            </Popover>
          </div>
        ),
      },
      {
        id: 'status',
        header: '状态',
        accessor: (row) => {
          const statusMap = {
            pending: { label: '未处理', variant: 'warning' as const },
            email_sent: { label: '已发送邮件', variant: 'success' as const },
            rejected: { label: '已拒绝', variant: 'destructive' as const },
          };
          const status = statusMap[row.status] ?? statusMap.pending;
          return (
            <span className="font-semibold">
              <Badge variant={status.variant}>{status.label}</Badge>
            </span>
          );
        },
      },
      {
        id: 'mailStatus',
        header: '邮件状态',
        accessor: (row) => {
          const status = getMailOutboxStatusMeta(row);
          return (
            <div className="flex items-center gap-2">
              <Badge variant={status.variant}>{status.label}</Badge>
              <Popover
                trigger="click"
                placement="leftTop"
                content={renderMailOutboxDetail(row)}
              >
                <Button variant="ghost" size="sm">
                  查看详情
                </Button>
              </Popover>
            </div>
          );
        },
      },
      {
        id: 'createdAt',
        header: '创建时间',
        accessor: (row) => formatDateTime(row.createdAt),
      },
      {
        id: 'updatedAt',
        header: '更新时间',
        accessor: (row) => formatDateTime(row.updatedAt),
      },
      {
        id: 'actions',
        header: '操作',
        accessor: (row) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onApprove(row)}
            disabled={row.status !== 'pending'}
          >
            <Send className="mr-1 h-4 w-4" />
            审批发送
          </Button>
        ),
      },
    ];

    return (
      <DataTable
        columns={columns}
        data={inviteApplicationStore.applications}
        loading={inviteApplicationStore.loading}
        emptyMessage="暂无申请"
      />
    );
  },
);
